// Aliased: this module already has `fr` (the French string bundle), and an
// unaliased date-fns import silently shadows it.
import { enUS as dfEn, ar as dfAr, fr as dfFr } from 'date-fns/locale';
import type { Locale as DateFnsLocale } from 'date-fns';
/**
 * Maternally i18n engine
 *
 * Features:
 *  - Zero dependencies (no i18next/react-intl overhead)
 *  - Type-safe key access via dot notation
 *  - {{variable}} interpolation
 *  - Full RTL/LTR switching (html dir + CSS class)
 *  - Persisted to localStorage
 *  - Extensible: add new locales by dropping a file in /locales/
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import en, { type Locale } from './locales/en';
import ar from './locales/ar';
import fr from './locales/fr';

// ─── Supported locales ────────────────────────────────────

export type SupportedLocale = 'en' | 'ar' | 'fr';

const LOCALES: Record<SupportedLocale, Locale> = { en, ar, fr };

/**
 * The date-fns locale matching the UI language.
 *
 * Dates and weekday names were being formatted with date-fns' default (English)
 * regardless of the interface language, so an Arabic-speaking mother saw her
 * mood chart labelled "Fri Sat Sun" and her screening dated "Aug 10". Static
 * copy was translated; anything formatted at runtime was not.
 */
export const DATE_FNS_LOCALE: Record<SupportedLocale, DateFnsLocale> = {
  en: dfEn,
  ar: dfAr,
  fr: dfFr,
};

export const LOCALE_META: Record<SupportedLocale, { label: string; dir: 'ltr' | 'rtl'; flag: string }> = {
  en: { label: 'English',  dir: 'ltr', flag: '🇺🇸' },
  ar: { label: 'العربية', dir: 'rtl', flag: '🇸🇦' },
  fr: { label: 'Français', dir: 'ltr', flag: '🇫🇷' },
};

const STORAGE_KEY  = 'maternally-locale';
const DEFAULT_LOCALE: SupportedLocale = 'en';

// ─── Type-safe dot-path accessor ─────────────────────────

type DotPaths<T, Prefix extends string = ''> = {
  [K in keyof T & string]: T[K] extends Record<string, unknown>
    ? DotPaths<T[K], `${Prefix}${K}.`> | `${Prefix}${K}`
    : `${Prefix}${K}`;
}[keyof T & string];

/**
 * Every valid dot-path into the English translation tree.
 *
 * English is the reference locale, so a key that exists there is the definition
 * of a real key. Typing t() with this turns a mistyped key from a silent
 * runtime failure — the app renders the raw string "dashbord.greeting" to a
 * mother — into a compile error.
 */
export type TranslationKey = DotPaths<typeof en>;

// ─── Interpolation ────────────────────────────────────────

function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  return template.replace(/\{\{(\w+)\}\}/g, (_, key) =>
    vars[key] !== undefined ? String(vars[key]) : `{{${key}}}`
  );
}

// ─── Deep get ─────────────────────────────────────────────

function deepGet(obj: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce((acc: unknown, key) => {
    if (acc && typeof acc === 'object') return (acc as Record<string, unknown>)[key];
    return undefined;
  }, obj);
}

// ─── Context ──────────────────────────────────────────────

interface I18nContext {
  locale:    SupportedLocale;
  dir:       'ltr' | 'rtl';
  isRTL:     boolean;
  t:         (key: TranslationKey, vars?: Record<string, string | number>) => string;
  setLocale: (locale: SupportedLocale) => void;
  strings:   Locale;
}

const Context = createContext<I18nContext | null>(null);

// ─── Provider ─────────────────────────────────────────────

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<SupportedLocale>(() => {
    const stored = localStorage.getItem(STORAGE_KEY) as SupportedLocale | null;
    if (stored && LOCALES[stored]) return stored;

    // Auto-detect from browser
    const browser = navigator.language.split('-')[0] as SupportedLocale;
    return LOCALES[browser] ? browser : DEFAULT_LOCALE;
  });

  const strings = LOCALES[locale];
  const dir     = strings.meta.dir as 'ltr' | 'rtl';
  const isRTL   = dir === 'rtl';

  // Apply dir + font to document root
  useEffect(() => {
    document.documentElement.setAttribute('dir',  dir);
    document.documentElement.setAttribute('lang', locale);
    document.documentElement.classList.toggle('rtl', isRTL);
    document.documentElement.classList.toggle('ltr', !isRTL);
  }, [locale, dir, isRTL]);

  const setLocale = useCallback((next: SupportedLocale) => {
    if (!LOCALES[next]) return;
    localStorage.setItem(STORAGE_KEY, next);
    setLocaleState(next);
  }, []);

  const t = useCallback((key: TranslationKey, vars?: Record<string, string | number>): string => {
    const value = deepGet(strings as unknown as Record<string, unknown>, key);
    if (typeof value === 'string') return interpolate(value, vars);

    // Fallback to English
    const fallback = deepGet(en as unknown as Record<string, unknown>, key);
    if (typeof fallback === 'string') {
      console.warn(`[i18n] Missing key "${key}" for locale "${locale}" — using English fallback`);
      return interpolate(fallback, vars);
    }

    console.error(`[i18n] Key "${key}" not found in any locale`);
    return key;
  }, [locale, strings]);

  return (
    <Context.Provider value={{ locale, dir, isRTL, t, setLocale, strings }}>
      {children}
    </Context.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────

export function useI18n() {
  const ctx = useContext(Context);
  if (!ctx) throw new Error('useI18n must be used inside <I18nProvider>');
  return ctx;
}

// ─── Convenience re-export ────────────────────────────────

export { LOCALES };
